import { API_BASE_URL, IS_DEV_TUNNEL, REQUEST_TIMEOUT_MS } from './config';

/**
 * Error thrown for every failed request so UI code can branch on `kind`:
 *  - 'network'  backend unreachable / offline / CORS
 *  - 'timeout'  no response within REQUEST_TIMEOUT_MS
 *  - 'http'     non-2xx response (see `status`)
 *  - 'parse'    2xx response whose body is not valid JSON
 */
export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {{ kind?: string, status?: number | null, body?: any }} [options]
   */
  constructor(message, { kind, status = null, body = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.body = body;
  }

  /** Validation problems the backend lists in `errors` (strings), or [] when there are none. */
  get details() {
    const list = this.body && Array.isArray(this.body.errors) ? this.body.errors : [];
    return list.filter((e) => typeof e === 'string' && e.trim() !== '');
  }
}

let authTokenProvider = () => null;

/**
 * Register a function returning the current auth token (or null): every request then sends
 * `Authorization: Bearer <token>`. `POST /login/auth` returns the token; AuthProvider registers it.
 */
export const setAuthTokenProvider = (fn) => {
  authTokenProvider = typeof fn === 'function' ? fn : () => null;
};

let unauthorizedHandler = () => {};

/**
 * Register what happens on a 401 (the token is invalid or expired): AuthProvider clears the session, which sends the
 * user to the sign-in page. Sign-in and sign-up are public, so a refusal there is not a session problem and is skipped.
 */
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = typeof fn === 'function' ? fn : () => {};
};

const PUBLIC_PATHS = ['/login/auth', '/login/register'];
const handleUnauthorized = (path) => {
  if (!PUBLIC_PATHS.includes(path)) unauthorizedHandler();
};

const buildUrl = (path, query) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, v);
    });
  }
  return url.toString();
};

/**
 * Returns a promise for the mock adapter in dev/test when REACT_APP_USE_MOCKS=true, otherwise null.
 * Deliberately a plain (non-async) function with the condition written inline: the bundler then resolves
 * it at build time and, in production builds, removes the branch AND the mocks chunk entirely.
 * (Inside an async function Babel turns the code into a state machine and the dead branch survives.)
 */
const loadMockAdapter = () => {
  if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCKS === 'true') {
    return import('./mocks/mockAdapter');
  }
  return null;
};

/**
 * request('/path', { method, body, query, signal, timeoutMs })
 * Resolves with the parsed JSON body, or `null` for an empty body
 * (the Spring API returns 200 + empty body for "not found" on some routes).
 * @param {string} path
 * @param {{ method?: string, body?: any, query?: any, signal?: AbortSignal, timeoutMs?: number }} [options]
 * @returns {Promise<any>}
 */
export async function request(path, { method = 'GET', body, query, signal, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
  const mocks = loadMockAdapter();
  if (mocks) {
    const { mockRequest } = await mocks;
    try {
      return await mockRequest({ method, path, body, query, token: authTokenProvider() });
    } catch (err) {
      if (err && err.status === 401) handleUnauthorized(path);
      throw err;
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort('timeout'), timeoutMs);
  if (signal) signal.addEventListener('abort', () => controller.abort(signal.reason));

  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (IS_DEV_TUNNEL) headers['X-Tunnel-Skip-AntiPhishing-Page'] = 'true';
  const token = authTokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted && controller.signal.reason === 'timeout') {
      throw new ApiError('The server took too long to respond.', { kind: 'timeout' });
    }
    if (signal?.aborted) throw err; // caller cancelled; let it propagate untouched
    throw new ApiError('Cannot reach the server. Check your connection or try again later.', { kind: 'network' });
  } finally {
    clearTimeout(timer);
  }

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      if (response.ok) throw new ApiError('The server returned an unreadable response.', { kind: 'parse', status: response.status });
    }
  }

  if (!response.ok) {
    // The backend's errors are { status, message } (login) or { timestamp, status, error, path } (everything else).
    const message = (data && (data.message || (data.error && `${data.error} (${data.status || response.status})`))) || `Request failed (${response.status}).`;
    if (response.status === 401) handleUnauthorized(path); // invalid or expired token: sign in again
    throw new ApiError(message, { kind: 'http', status: response.status, body: data });
  }
  return data;
}

/** The file name in a Content-Disposition header (`filename="x.csv"` or `filename*=UTF-8''x.csv`), or null. */
export function filenameFrom(header) {
  if (!header) return null;
  const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch (e) { return star[1].trim(); }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/**
 * Download a non-JSON response (the respondents CSV) as a Blob.
 * A plain `<a href>` would skip the auth header (needed the day the server enforces tokens, AUTH_REQUIRED) and hit the
 * dev tunnel's warning page, so this is a fetch that sends the same headers as `request`.
 * Errors come back as ApiError exactly as for `request` (the body of a failure is JSON `{ message, errors }`).
 * Resolves { blob, filename }: `filename` comes from the server's Content-Disposition header (the backend exposes it through CORS now) and is
 * null when the header is missing or cannot be read, so the caller keeps its own fallback name.
 * @param {string} path
 * @param {{ query?: any, signal?: AbortSignal, timeoutMs?: number }} [options]
 * @returns {Promise<{ blob: Blob, filename: string | null }>}
 */
export async function requestBlob(path, { query, signal, timeoutMs = 60000 } = {}) {
  const mocks = loadMockAdapter();
  if (mocks) {
    const { mockRequest } = await mocks;
    let text;
    try {
      text = await mockRequest({ method: 'GET', path, query, token: authTokenProvider() });
    } catch (err) {
      if (err && err.status === 401) handleUnauthorized(path);
      throw err;
    }
    return { blob: new Blob([typeof text === 'string' ? text : ''], { type: 'text/csv' }), filename: null };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort('timeout'), timeoutMs);
  if (signal) signal.addEventListener('abort', () => controller.abort(signal.reason));

  const headers = {};
  if (IS_DEV_TUNNEL) headers['X-Tunnel-Skip-AntiPhishing-Page'] = 'true';
  const token = authTokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response;
  try {
    response = await fetch(buildUrl(path, query), { method: 'GET', headers, signal: controller.signal });
  } catch (err) {
    if (controller.signal.aborted && controller.signal.reason === 'timeout') {
      throw new ApiError('The server took too long to respond.', { kind: 'timeout' });
    }
    if (signal?.aborted) throw err;
    throw new ApiError('Cannot reach the server. Check your connection or try again later.', { kind: 'network' });
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let data = null;
    try { data = JSON.parse(await response.text()); } catch (e) { /* not JSON: use the generic message */ }
    const message = (data && (data.message || (data.error && `${data.error} (${data.status || response.status})`))) || `Request failed (${response.status}).`;
    if (response.status === 401) handleUnauthorized(path);
    throw new ApiError(message, { kind: 'http', status: response.status, body: data });
  }
  return { blob: await response.blob(), filename: filenameFrom(response.headers.get('Content-Disposition')) };
}

/** Friendly text for any thrown error, for display in the UI. */
export const errorMessage = (err) => {
  if (!(err instanceof ApiError)) return 'Something went wrong. Please try again.';
  // Validation errors say what is wrong in `errors`; show it, not just "is not valid".
  return err.details.length ? `${err.message}: ${err.details.join('; ')}` : err.message;
};

/** Every line worth showing for an error: the message, then the backend's validation problems (for lists). */
export const errorLines = (err) => {
  if (!(err instanceof ApiError)) return [errorMessage(err)];
  return err.details.length ? [err.message, ...err.details] : [err.message];
};
